import type { ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";

import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { NotEnoughQuotaTooltip } from "app/pages/sales-intelligence/modules/quota/components/NotEnoughQuotaTooltip";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { isNoTouch, isTrial } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import useRequestFeatureHookWithLoading from "../../../../hooks/useRequestFeatureHookWithLoading";
import { toggleContactUsHook } from "../../store/action-creators";
import { selectIsContactUsHookUsed } from "../../store/selectors";

import { StyledRevealButton } from "./styles";

export interface IContactRevealButtonStateBaseProps {
    isLoading: boolean;
    isDisabledTooltip?: boolean;
    isDataCreditsEnabled: boolean;
    buttonType?: "primary" | "flat";
    remainingRevealQuota: number;
    contactsCost: number;
    onReveal: VoidFunction;
    onContactUs?: VoidFunction;
}

const ContactRevealButtonState = ({
    isLoading,
    isDisabledTooltip,
    isDataCreditsEnabled,
    buttonType = "primary",
    remainingRevealQuota,
    contactsCost,
    onReveal,
    onContactUs,
}: IContactRevealButtonStateBaseProps): ReactElement | null => {
    const dispatch = useDispatch();
    const isContactUsHookUsed = useSelector(selectIsContactUsHookUsed);

    const isAdmin = salesSettingsHelper.isAdmin();
    const isNoTouchUser = isNoTouch(swSettings);
    const isTrialUser = isTrial(swSettings);

    const { isRequestInProgress, requestFeature } = useRequestFeatureHookWithLoading();

    const remainingRevealItems = Math.floor(remainingRevealQuota / contactsCost);

    const isRevealDisabled = remainingRevealItems <= 0;
    const isContactUsRequested = isContactUsHookUsed && !isRequestInProgress;

    const handleContactUsHook = () => {
        if (isRequestInProgress || isContactUsHookUsed) {
            return;
        }

        requestFeature("SI contact").then(() => {
            onContactUs?.();
        });

        dispatch(toggleContactUsHook());
    };

    const renderTooltipActionText = () =>
        isAdmin ? (
            <Text
                cursor="pointer"
                fontVariant="tooltip"
                color={colorsPalettes.blue[300]}
                onClick={handleContactUsHook}
            >
                {translate("si.common.contact_us")}
                {isRequestInProgress ? "..." : ""}
            </Text>
        ) : (
            <Text
                fontVariant="tooltip"
                color={colorsPalettes.bluegrey[100]}
            >
                {translate("si.common.contact_admin")}
            </Text>
        );

    const renderNoQuotaTooltip = (): ReactElement => (
        <Flex flexDirection="column">
            <Text
                fontVariant="tooltip"
                color={colorsPalettes.bluegrey[100]}
            >
                {translate(
                    isContactUsRequested
                        ? "si.contacts_search.no-remaining-quota.requested"
                        : "si.contacts_search.no-remaining-quota",
                )}
            </Text>
            {!isContactUsRequested ? renderTooltipActionText() : null}
        </Flex>
    );

    const button = (
        <div>
            <StyledRevealButton
                type={buttonType}
                onClick={onReveal}
                isLoading={isLoading}
                isDisabled={isLoading || isRevealDisabled}
            >
                {translate("si.advanced_search.results_section.contacts.button.reveal")}
            </StyledRevealButton>
        </div>
    );

    if (isDataCreditsEnabled && isRevealDisabled) {
        return (
            <NotEnoughQuotaTooltip
                quota={remainingRevealQuota}
                isAdmin={isAdmin}
                isNoTouch={isNoTouchUser}
                isTrial={isTrialUser}
            >
                {button}
            </NotEnoughQuotaTooltip>
        );
    }

    const revealTooltipText = isRevealDisabled
        ? renderNoQuotaTooltip()
        : translate(
              isDataCreditsEnabled
                  ? "si.contacts_search.contact-data-credits-left"
                  : "si.contacts_search.contact-credits-left",
              {
                  count: remainingRevealQuota,
                  cost: contactsCost,
              },
          );

    return (
        <Tooltip
            content={revealTooltipText}
            enabled={!isDisabledTooltip}
        >
            {button}
        </Tooltip>
    );
};

export default ContactRevealButtonState;
